import { defaultFilter } from "cmdk";
import {
  type ComponentProps,
  type FC,
  type ForwardedRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { z } from "zod";

import {
  Button,
  Chip,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useCommandState,
} from "../../components";
import { cn, setRefs } from "../../utils";
import { FIELD_BORDERLESS_CLASS_NAME, FIELD_READ_ONLY_CLASS_NAME } from "./field.constants";
import { FieldBase, type FieldBaseProps } from "./field-base";
import { useField } from "./use-field";

const defaultItemValidationSchema = z.string().min(1);

export interface FieldTagsOption {
  label: string;
  value: string;
  displayLabel?: string;
  disabled?: boolean;
  /** Ranks this option above non-priority options whenever it matches the typed text. */
  priority?: boolean;
}

// cmdk trims item values, so the filter must look them up trimmed too.
const getOptionItemValue = (option: FieldTagsOption) => `${option.label}, ${option.value}`.trim();

export type FieldTagsItemValue = FieldTagsOption;

export interface FieldTagsProps extends FieldBaseProps {
  placeholder?: string;
  borderless?: boolean;
  createable?: boolean;
  options?: FieldTagsOption[];
  separator?: string;
  itemValidationSchema?: z.ZodSchema<any>;
  onChange?: (value: string[]) => void;
  optionLabelComponent?: FC<{ option: FieldTagsOption }>;
  inputProps?: ComponentProps<"input">;
}

export const FieldTags: FC<FieldTagsProps> = ({
  options,
  separator = ",",
  itemValidationSchema = defaultItemValidationSchema,
  readOnly,
  className,
  borderless,
  placeholder,
  createable = true,
  onChange,
  optionLabelComponent,
  inputProps,
  ...props
}) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { field, hasError } = useField(props);

  const defaultInternalValue = useMemo(() => {
    if (Array.isArray(field.value)) {
      return field.value.map((item) => {
        const option = options?.find((option) => option.value === item);
        return { label: option?.label || item, value: item, displayLabel: option?.displayLabel || item };
      });
    }
    return [];
  }, [field.value, options]);

  const [inputValue, setInputValue] = useState("");
  const [internalValue, setInternalValue] = useState<FieldTagsItemValue[]>(defaultInternalValue);
  const internalValueRef = useRef<FieldTagsItemValue[]>(defaultInternalValue);

  // Sync internal value with external form value changes
  useEffect(() => {
    internalValueRef.current = defaultInternalValue;
    setInternalValue(defaultInternalValue);
  }, [defaultInternalValue]);

  const hasValue = !!internalValue?.length;
  const hasOptions = !!options?.length;

  const isValidValue = (value: string) => itemValidationSchema.safeParse(value).success;

  const handleChange = (tags: FieldTagsItemValue[]) => {
    internalValueRef.current = tags;
    setInternalValue(tags);
    field.onChange(tags.map((item) => item.value));
    onChange?.(tags.map((item) => item.value));
  };

  const handleChangeWithLatestValue = (updater: (currentTags: FieldTagsItemValue[]) => FieldTagsItemValue[]) => {
    const nextTags = updater(internalValueRef.current);
    handleChange(nextTags);
  };

  const handleDeleteItem = (index: number) => {
    handleChangeWithLatestValue((currentTags) => currentTags.filter((_: any, i: number) => i !== index));
  };

  const handleAddItem = (item: string) => {
    if (!isValidValue(item)) {
      return;
    }

    handleChangeWithLatestValue((currentTags) => [...currentTags, { label: item, value: item }]);
  };

  const handleSelectOption = (option: FieldTagsOption) => {
    handleChangeWithLatestValue((currentTags) => [...currentTags, { ...option }]);
    setInputValue("");
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (field.disabled) {
      return;
    }

    if (e.key === separator && e.currentTarget.value && e.currentTarget.value !== separator) {
      if (!isValidValue(e.currentTarget.value)) {
        return;
      }

      handleAddItem(e.currentTarget.value);
      setInputValue("");
    }

    if (e.key === "Enter" && e.currentTarget.value && !options?.length) {
      e.preventDefault();

      if (!isValidValue(e.currentTarget.value)) {
        return;
      }

      handleAddItem(e.currentTarget.value);
      setInputValue("");
    }

    if (e.key === "Backspace" && !e.currentTarget.value && hasValue) {
      handleDeleteItem(internalValue.length - 1);
    }
  };

  const handleInputChange = (value: string) => {
    if (value && value.endsWith(separator)) {
      setInputValue(value.slice(0, -1));
      return;
    }

    setInputValue(value);
  };

  const handleInputBlur = () => {
    if (inputValue) {
      // Preserve expected form behavior by committing valid typed entries on blur.
      if (createable && isValidValue(inputValue)) {
        handleAddItem(inputValue);
      }
      setInputValue("");
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
    if (e.target instanceof HTMLInputElement) {
      return;
    }
    e.stopPropagation();
    inputRef.current?.focus();
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.target instanceof HTMLInputElement) {
      return;
    }
    e.stopPropagation();
    inputRef.current?.click();
    inputRef.current?.focus();
  };

  const combinedInputProps = {
    ...inputProps,
    ref: setRefs(inputProps?.ref as ForwardedRef<HTMLInputElement | null>, inputRef),
    onBlur: handleInputBlur,
    onKeyDown: handleInputKeyDown,
    onValueChange: handleInputChange,
    disabled: field.disabled,
    value: inputValue,
    placeholder: hasValue ? "" : placeholder,
    hasValue,
    isValidValue,
    borderless,
  };

  return (
    <FieldBase ref={fieldRef} {...props}>
      <Button
        ref={setRefs(field.ref, fieldRef as any)}
        variant="outline"
        role="combobox"
        disabled={field.disabled}
        onClick={handleClick}
        onFocus={handleFocus}
        asChild
        className={cn(
          "bg-background w-auto z-10 h-auto min-h-10 p-1.5 justify-start font-normal gap-2",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          {
            "text-muted-foreground": !hasValue,
            "border-destructive": hasError && !field.disabled,
            [FIELD_BORDERLESS_CLASS_NAME]: borderless,
            [FIELD_READ_ONLY_CLASS_NAME]: readOnly,
          },
          className
        )}
      >
        <div className="max-w-full">
          <ul className="w-full flex flex-wrap items-center gap-1">
            {internalValue?.map?.((tag: FieldTagsItemValue, index: number) => (
              <li key={index} className="flex-0 max-w-full">
                <FieldTagsChip tag={tag} disabled={field.disabled} onDelete={() => handleDeleteItem(index)} />
              </li>
            ))}

            <li className="flex-1">
              {hasOptions ? (
                <FieldTagsInputWithOptions
                  options={options}
                  onSelectOption={handleSelectOption}
                  createable={createable}
                  optionLabelComponent={optionLabelComponent}
                  {...combinedInputProps}
                />
              ) : (
                <FieldTagsInput {...combinedInputProps} />
              )}
            </li>
          </ul>
        </div>
      </Button>
    </FieldBase>
  );
};

interface FieldTagsChipProps {
  tag: FieldTagsItemValue;
  disabled?: boolean;
  onDelete: () => void;
}

/**
 * A tag chip that discloses the underlying value on hover when the visible label hides it, e.g. a
 * contact display name standing in for an email address. Chips are truncated, so the tooltip is
 * also what makes a clipped label readable in full.
 */
const FieldTagsChip: FC<FieldTagsChipProps> = ({ tag, disabled, onDelete }) => {
  const label = tag.displayLabel || tag.label;

  const chip = (
    <Chip onDelete={onDelete} disabled={disabled} deletable tabIndex={-1}>
      {label}
    </Chip>
  );

  if (label === tag.value) {
    return chip;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{chip}</TooltipTrigger>
      <TooltipContent>{tag.value}</TooltipContent>
    </Tooltip>
  );
};

interface FieldTagsInputProps extends Omit<ComponentProps<"input">, "onChange"> {
  hasValue: boolean;
  borderless?: boolean;
  onValueChange?: (value: string) => void;
  isValidValue: (value: string) => boolean;
}

const FieldTagsInput: FC<FieldTagsInputProps> = ({ hasValue, onValueChange, isValidValue, ...props }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onValueChange?.(e.target.value);
  };

  return (
    <input
      {...props}
      onChange={handleChange}
      className={cn(
        "h-6.5! min-w-20! appearance-none outline-none text-foreground placeholder:text-muted-foreground border-none w-full px-1.5",
        { "px-0.5": hasValue }
      )}
    />
  );
};

interface FieldTagsInputWithOptionsProps extends FieldTagsInputProps {
  options: FieldTagsOption[];
  onSelectOption: (option: FieldTagsOption) => void;
  createable?: boolean;
  optionLabelComponent?: FC<{ option: FieldTagsOption }>;
}

const FieldTagsInputWithOptions: FC<FieldTagsInputWithOptionsProps> = ({
  ref,
  options,
  onSelectOption,
  hasValue,
  borderless,
  isValidValue,
  createable,
  optionLabelComponent: OptionLabelComponent,
  ...props
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const onBlurRef = useRef(props.onBlur);
  const [isOpen, setIsOpen] = useState(false);

  const priorityItemValues = useMemo(
    () => new Set(options.filter((option) => option.priority).map(getOptionItemValue)),
    [options]
  );

  // cmdk expects scores in [0, 1] with 0 meaning hidden. Matches split that range: ordinary ones
  // into (0, 0.5], priority ones into (0.5, 1], each keeping cmdk's fuzzy order within its half.
  const filter = useCallback(
    (value: string, search: string, keywords?: string[]) => {
      const score = defaultFilter(value, search, keywords);
      return score > 0 && priorityItemValues.has(value) ? (1 + score) / 2 : score / 2;
    },
    [priorityItemValues]
  );

  useEffect(() => {
    onBlurRef.current = props.onBlur;
  }, [props.onBlur]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsOpen(true);
    props.onFocus?.(e);
  };

  const handleClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    setIsOpen(true);
    props.onClick?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Add slight delay so the value will be set if an option was selected via click.
    setTimeout(() => {
      onBlurRef.current?.(e);
    }, 500);
  };

  const handleSelectOption = (option: FieldTagsOption) => {
    if (!props.value) {
      return;
    }
    onSelectOption(option);
  };

  const handleValueChange = (value: string) => {
    props.onValueChange?.(value);

    // IMPORTANT: This is a hack, but it is necessary, because the CommandList component does
    // not always scroll properly and sometimes ends up scrolling someplace random.
    setTimeout(() => scrollToHighlightedItem(), 10);
  };

  // Scroll the highlighted item into view within the list.
  const scrollToHighlightedItem = () => {
    if (listRef.current) {
      const listHeight = listRef.current.offsetHeight;
      const highlightedItem = listRef.current.querySelector('[data-selected="true"]') as unknown as HTMLElement | null;
      const highlightedItemHeight = highlightedItem?.offsetHeight || 0;
      const highlightedItemOffsetTop = highlightedItem?.offsetTop || 0;
      const highlightedItemBottom = highlightedItemOffsetTop + highlightedItemHeight;

      const scrollTo = highlightedItemBottom > listHeight ? highlightedItemBottom - listHeight : 0;

      listRef.current?.scrollTo({ top: scrollTo, behavior: "instant" as ScrollBehavior });
    }
  };

  return (
    <Command loop filter={filter} className="bg-transparent!">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild className="w-full bg-transparent! p-0!">
          <div>
            <CommandInput
              {...props}
              ref={ref}
              value={props.value as string}
              onValueChange={handleValueChange}
              hasSearchIcon={false}
              onFocus={handleFocus}
              onClick={handleClick}
              onBlur={handleBlur}
              className={cn("[&_input]:h-6.5! px-1.5 min-w-20", {
                "px-0.5": hasValue,
                "pl-0": borderless && !hasValue,
              })}
            />
          </div>
        </PopoverTrigger>

        <FieldTagsPopoverContent className={cn({ ["hidden!"]: !(props.value as string)?.length })}>
          <FieldTagsCommandList ref={listRef}>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={getOptionItemValue(option)}
                  onSelect={() => handleSelectOption(option)}
                  disabled={option.disabled}
                >
                  {OptionLabelComponent ? <OptionLabelComponent option={option} /> : option.label}
                </CommandItem>
              ))}
            </CommandGroup>

            {createable && (
              <FieldTagsCommandCreateItem
                inputValue={props.value as string}
                isValidValue={isValidValue}
                onSelectOption={handleSelectOption}
              />
            )}
          </FieldTagsCommandList>
        </FieldTagsPopoverContent>
      </Popover>
    </Command>
  );
};

const FieldTagsCommandCreateItem: FC<
  ComponentProps<typeof CommandItem> & {
    inputValue: string;
    isValidValue: (value: string) => boolean;
    onSelectOption: (option: FieldTagsOption) => void;
  }
> = ({ className, inputValue, isValidValue, onSelectOption, ...props }) => {
  const filteredCount = useCommandState((state) => state.filtered.count);

  const createOption = useMemo(
    () => (isValidValue?.(inputValue) ? { label: `${inputValue}`, value: `${inputValue}` } : null),
    [inputValue, isValidValue]
  );

  if (!createOption) {
    return null;
  }

  return (
    <CommandGroup className={cn({ "pt-0! -mt-1": filteredCount > 1 })}>
      <CommandItem value={`Add "${createOption.label}"`} onSelect={() => onSelectOption(createOption)} {...props}>
        Add "{createOption.label}"
      </CommandItem>
    </CommandGroup>
  );
};

const FieldTagsPopoverContent: FC<ComponentProps<typeof PopoverContent>> = ({ className, ...props }) => {
  const filteredCount = useCommandState((state) => state.filtered.count);
  return (
    <PopoverContent
      align="start"
      className={cn("p-0 w-auto min-w-40 max-w-none!", { "hidden!": filteredCount < 1 }, className)}
      {...props}
    />
  );
};

const FieldTagsCommandList: FC<ComponentProps<typeof CommandList>> = ({ className, ...props }) => {
  const filteredCount = useCommandState((state) => state.filtered.count);
  return <CommandList className={cn("max-h-60", { "hidden!": filteredCount === 0 }, className)} {...props} />;
};
